/**
 * Rate Connect guided tour composable
 * Manages tour state, navigation, and step progression
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue'
import { useRouter, type Router } from 'vue-router'
import { selectedAccommodationId } from '@/layouts/queries'
import type { TourStep } from '@/constants/rateConnectTourSteps'

import appAnalytics from '@/utils/tracking'



/** Shared tour state (global across components) */
const isActive = ref<boolean>(false)
const currentStepIndex = ref<number>(0)
const tourSteps = ref<TourStep[]>([])

/** Return type of useTour composable */
export interface UseTourReturn {
  isActive: Ref<boolean>
  currentStep: ComputedRef<TourStep | undefined>
  currentStepIndex: Ref<number>
  isLastStep: ComputedRef<boolean>
  isFirstStep: ComputedRef<boolean>
  tourSteps: Ref<TourStep[]>
  startTour: (steps: TourStep[]) => void
  nextStep: () => Promise<void>
  previousStep: () => Promise<void>
  endTour: () => void
  skipTour: () => void
}

/**
 * Rate Connect tour management
 * Provides tour navigation, state tracking, and analytics
 * 
 * @returns Tour control functions and state
 * 
 * @example
 * const { startTour, nextStep, isActive } = useTour()
 * startTour(RATE_CONNECT_TOUR_STEPS)
 */
export function useTour(): UseTourReturn {
  const router: Router = useRouter()

  const currentStep = computed<TourStep | undefined>(() => tourSteps.value[currentStepIndex.value])

  const isLastStep = computed<boolean>(() => currentStepIndex.value === tourSteps.value.length - 1)

  const isFirstStep = computed<boolean>(() => currentStepIndex.value === 0)

  /**
   * Start tour with provided steps
   * @param steps - Array of tour step configurations
   */
  const startTour = (steps: TourStep[]): void => {
    tourSteps.value = steps
    currentStepIndex.value = 0
    isActive.value = true

    if (steps[0]?.route) {
      router.push(steps[0].route)
    }
  }

  /**
   * Move to next tour step
   * Ends tour if on last step
   */
  const nextStep = async (): Promise<void> => {
    // If last step, end the tour
    if (isLastStep.value) {
      appAnalytics.track(appAnalytics.events.RC_END_TOUR_CLICKED, {
        itemId: selectedAccommodationId.value,
      })
      endTour()
      return
    }

    currentStepIndex.value++
    if (currentStep.value?.route) {
      await router.push(currentStep.value.route)
    }
  }

  /**
   * Move to previous tour step
   * No-op if on first step
   */
  const previousStep = async (): Promise<void> => {
    if (!isFirstStep.value) {
      currentStepIndex.value--

      if (currentStep.value?.route) {
        await router.push(currentStep.value.route)
      }
    }
  }

  /**
   * End tour and reset state
   * Returns to Rate Connect performance page
   */
  const endTour = (): void => {
    isActive.value = false
    currentStepIndex.value = 0
    tourSteps.value = []
    router.push({ name: 'rate-connect-running-performance' })
  }

  /**
   * Skip/cancel tour
   * Tracks analytics if on last step
   */
  const skipTour = (): void => {
    if (isLastStep.value) {
      appAnalytics.track(appAnalytics.events.RC_END_TOUR_CLICKED, {
        itemId: selectedAccommodationId.value,
      })
    }
    endTour()
  }

  return {
    isActive,
    currentStep,
    currentStepIndex,
    isLastStep,
    isFirstStep,
    tourSteps,
    startTour,
    nextStep,
    previousStep,
    endTour,
    skipTour,
  }
}